import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Key, Copy, Trash2, Eye, EyeOff, Plus, RefreshCw, Shield } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  permissions: string[];
  is_active: boolean;
  last_used_at: string | null;
  request_count: number;
  rate_limit_per_minute: number;
  created_at: string;
}

export const CRMApiSettings = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>(["read"]);
  const [newKeyRateLimit, setNewKeyRateLimit] = useState(100);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showGeneratedKey, setShowGeneratedKey] = useState(false);
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const { data, error } = await supabase
        .from('crm_api_keys')
        .select('id, name, key_prefix, permissions, is_active, last_used_at, request_count, rate_limit_per_minute, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast({
        title: "Error",
        description: "Failed to load API keys",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateApiKey = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = 'crm_';
    for (let i = 0; i < 48; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
  };

  const hashApiKey = async (key: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(key);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a name for the API key",
        variant: "destructive",
      });
      return;
    }

    setCreating(true);
    try {
      const rawKey = generateApiKey();
      const keyHash = await hashApiKey(rawKey);
      const keyPrefix = rawKey.substring(0, 8);

      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('crm_api_keys')
        .insert({
          name: newKeyName.trim(),
          key_hash: keyHash,
          key_prefix: keyPrefix,
          permissions: newKeyPermissions,
          rate_limit_per_minute: newKeyRateLimit,
          created_by: user?.id,
        });

      if (error) throw error;

      setGeneratedKey(rawKey);
      await fetchApiKeys();
      
      toast({
        title: "API Key Created",
        description: "Copy your key now - it won't be shown again!",
      });
    } catch (error) {
      console.error('Error creating API key:', error);
      toast({
        title: "Error",
        description: "Failed to create API key",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const toggleKeyStatus = async (keyId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('crm_api_keys')
        .update({ is_active: !currentStatus })
        .eq('id', keyId);

      if (error) throw error;
      
      await fetchApiKeys();
      toast({
        title: currentStatus ? "Key Deactivated" : "Key Activated",
        description: `API key has been ${currentStatus ? 'deactivated' : 'activated'}`,
      });
    } catch (error) {
      console.error('Error toggling key status:', error);
      toast({
        title: "Error",
        description: "Failed to update API key",
        variant: "destructive",
      });
    }
  };

  const deleteApiKey = async (keyId: string) => {
    try {
      const { error } = await supabase
        .from('crm_api_keys')
        .delete()
        .eq('id', keyId);

      if (error) throw error;
      
      await fetchApiKeys();
      toast({
        title: "API Key Deleted",
        description: "The API key has been permanently deleted",
      });
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast({
        title: "Error",
        description: "Failed to delete API key",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "Copied to clipboard",
    });
  };

  const resetCreateDialog = () => {
    setNewKeyName("");
    setNewKeyPermissions(["read"]);
    setNewKeyRateLimit(100);
    setGeneratedKey(null);
    setShowGeneratedKey(false);
    setCreateDialogOpen(false);
  };

  const togglePermission = (permission: string) => {
    if (newKeyPermissions.includes(permission)) {
      setNewKeyPermissions(newKeyPermissions.filter(p => p !== permission));
    } else {
      setNewKeyPermissions([...newKeyPermissions, permission]);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return "Never";
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              CRM API Access
            </CardTitle>
            <CardDescription>
              Generate API keys to access CRM data from other applications
            </CardDescription>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => resetCreateDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Create API Key
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New API Key</DialogTitle>
                <DialogDescription>
                  Generate a new API key for external access to CRM data
                </DialogDescription>
              </DialogHeader>

              {!generatedKey ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="keyName">Key Name</Label>
                    <Input
                      id="keyName"
                      placeholder="e.g., Production App, Development"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Permissions</Label>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="perm-read"
                          checked={newKeyPermissions.includes("read")}
                          onCheckedChange={() => togglePermission("read")}
                        />
                        <label htmlFor="perm-read" className="text-sm">
                          Read - View contacts and activities
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="perm-write"
                          checked={newKeyPermissions.includes("write")}
                          onCheckedChange={() => togglePermission("write")}
                        />
                        <label htmlFor="perm-write" className="text-sm">
                          Write - Create and update records
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="perm-admin"
                          checked={newKeyPermissions.includes("admin")}
                          onCheckedChange={() => togglePermission("admin")}
                        />
                        <label htmlFor="perm-admin" className="text-sm">
                          Admin - Full access including delete
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="rateLimit">Rate Limit (requests/minute)</Label>
                    <Input
                      id="rateLimit"
                      type="number"
                      min={10}
                      max={1000}
                      value={newKeyRateLimit}
                      onChange={(e) => setNewKeyRateLimit(parseInt(e.target.value) || 100)}
                    />
                  </div>

                  <Button 
                    onClick={createApiKey} 
                    className="w-full"
                    disabled={creating || !newKeyName.trim() || newKeyPermissions.length === 0}
                  >
                    {creating ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Key className="h-4 w-4 mr-2" />
                        Generate API Key
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200 mb-2">
                      <Shield className="h-4 w-4" />
                      <span className="font-medium">Copy your API key now!</span>
                    </div>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      This key will not be shown again. Store it securely.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Your API Key</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type={showGeneratedKey ? "text" : "password"}
                        value={generatedKey}
                        readOnly
                        className="font-mono text-sm"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setShowGeneratedKey(!showGeneratedKey)}
                      >
                        {showGeneratedKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(generatedKey)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>API Endpoint</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={`${supabaseUrl}/functions/v1/crm-api`}
                        readOnly
                        className="font-mono text-sm"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(`${supabaseUrl}/functions/v1/crm-api`)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <Button onClick={resetCreateDialog} className="w-full">
                    Done
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading API keys...</div>
        ) : apiKeys.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No API keys created yet</p>
            <p className="text-sm mt-1">Create an API key to access CRM data from other apps</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key Prefix</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Requests</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell className="font-medium">{key.name}</TableCell>
                  <TableCell className="font-mono text-sm">{key.key_prefix}...</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {key.permissions.map((perm) => (
                        <Badge key={perm} variant="secondary" className="text-xs">
                          {perm}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{key.request_count.toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(key.last_used_at)}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={key.is_active}
                      onCheckedChange={() => toggleKeyStatus(key.id, key.is_active)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete API Key?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete the API key "{key.name}". 
                            Any applications using this key will no longer be able to access the CRM.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteApiKey(key.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* API Documentation Section */}
        <div className="mt-8 pt-6 border-t">
          <h4 className="font-medium mb-4">Quick Start</h4>
          <div className="bg-muted p-4 rounded-lg font-mono text-sm overflow-x-auto">
            <pre className="text-foreground">{`// Example: Fetch contacts from another app
const response = await fetch(
  '${supabaseUrl}/functions/v1/crm-api/contacts',
  {
    headers: {
      'X-API-Key': 'your-api-key-here'
    }
  }
);
const { data } = await response.json();`}</pre>
          </div>

          <h4 className="font-medium mt-6 mb-2">Available Endpoints</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li><code className="bg-muted px-1 rounded">GET /contacts</code> - List contacts (with pagination)</li>
            <li><code className="bg-muted px-1 rounded">GET /contacts/:id</code> - Get single contact</li>
            <li><code className="bg-muted px-1 rounded">POST /contacts</code> - Create contact</li>
            <li><code className="bg-muted px-1 rounded">PATCH /contacts/:id</code> - Update contact</li>
            <li><code className="bg-muted px-1 rounded">DELETE /contacts/:id</code> - Delete contact (admin only)</li>
            <li><code className="bg-muted px-1 rounded">GET /contacts/:id/activities</code> - Get contact activities</li>
            <li><code className="bg-muted px-1 rounded">POST /contacts/:id/activities</code> - Log activity</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
